import { useGetVenteIdQuery } from "actions/ventes/queries";
import { useGetProduitsQuery } from "actions/produits/queries";
import { useGetCodesQuery } from "actions/comptabilite/queries";
import AutocompleteSelect from "components/AutocompleteSelect/AutocompleteSelect";
import FilePreview from "components/FilePreview/FilePreview";
import InputDetail from "components/Inputs/InputDetail";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import HeaderFirst from "utils/HeaderFirst";
import "./client.css";
import useVentesData from "./useVentesData";
// import { DEFAULT_API_IMAGE } from 'routes/api/endpoints';

const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString)
    .toLocaleDateString("fr-FR", options)
    .replace(",", "");
};

const DetailModifPage = () => {
  const { id } = useParams();
  const { clientID: dossier_id } = useParams();
  const navigate = useNavigate();
  const {
    data: venteDetail,
    isLoading: isLoadingVente,
    error: venteError,
  } = useGetVenteIdQuery({ id, dossier_id });
  const { handleUpdateVente } = useVentesData(id);
  const { data: produitsData } = useGetProduitsQuery({ dossier_id });
  const { data: codesData } = useGetCodesQuery({ dossier_client: dossier_id });

  // Comptes proposables pour une vente : classe 7 (produits) + 8 (HAO/cessions)
  const rawCodes = Array.isArray(codesData)
    ? codesData
    : codesData?.data || codesData?.results || [];
  const accountOptions = rawCodes
    .filter((c) => {
      const s = String(c.code ?? "");
      return s.startsWith("7") || s.startsWith("8");
    })
    .map((c) => ({ id: Number(c.code), nom: c.combine || `${c.code} - ${c.libelle}` }));

  const [updateData, setUpdateData] = useState({
    date: "",
    intitule: "",
    description: "",
    produits_associes: [],
  });
  const [calculatedTotal, setCalculatedTotal] = useState(0);

  useEffect(() => {
    // Check if venteDetail.data exists and has at least one item
    if (venteDetail && venteDetail.data && venteDetail.data.length > 0) {
      const venteData = venteDetail.data[0];
      const enrichedProduits = (venteData.produits_associes || []).map(
        (prodAssoc) => {
          // Utiliser directement le nom de la réponse backend (MODIFIÉ)
          return {
            id: prodAssoc.produit, // Keep the product ID
            nom: prodAssoc.nom || "Produit inconnu", // Utiliser directement prodAssoc.nom
            quantite: prodAssoc.quantite?.toString() || "", // Convert quantite to string
            prix: prodAssoc.prix?.toString() || "", // Convert prix to string
            code_comptable: prodAssoc.code_comptable ?? null,
          };
        },
      );

      setUpdateData({
        date: formatDate(venteData.updated_at),
        produits_associes: enrichedProduits,
        montant_total: venteData.montant_total.toString(),
        description: venteData.description,
        intitule: venteData.intitule || "",
      });
    }
  }, [venteDetail]);

  // Recalculer le total automatiquement quand les produits changent
  useEffect(() => {
    if (updateData.produits_associes && updateData.produits_associes.length > 0) {
      const total = updateData.produits_associes.reduce((sum, prod) => {
        const quantite = parseFloat(prod.quantite) || 0;
        const prix = parseFloat(prod.prix) || 0;
        return sum + (quantite * prix);
      }, 0);
      setCalculatedTotal(total);
    } else {
      setCalculatedTotal(0);
    }
  }, [updateData.produits_associes]);

  const handleQuantiteChange = (index, newQuantite) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].quantite = newQuantite.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handlePrixChange = (index, newPrix) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index].prix = newPrix.toString();
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleProduitChange = (index, value) => {
    // Ne garder que les ids numeriques (selection dans la liste)
    if (typeof value !== "number") return;
    const prod = (produitsData?.data || []).find((p) => p.id === value);
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        id: value,
        nom: prod?.nom || updatedProduits[index].nom,
        code_comptable:
          updatedProduits[index].code_comptable ?? prod?.code_comptable ?? null,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const handleCompteChange = (index, value) => {
    setUpdateData((prevState) => {
      const updatedProduits = [...prevState.produits_associes];
      updatedProduits[index] = {
        ...updatedProduits[index],
        code_comptable: value,
      };
      return { ...prevState, produits_associes: updatedProduits };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const submitData = {
      ...updateData,
      montant_total: calculatedTotal, // Envoyer le total calculé
      produits_associes: updateData.produits_associes.map((prod) => {
        const code = String(prod.code_comptable ?? "").replace(/\D/g, "");
        return {
          produit: prod.id, // Assuming prod.id contains the product ID.
          quantite: parseFloat(prod.quantite), // Ensure quantite is a number.
          prix: prod.prix.toString(), // Ensure prix is a string.
          code_comptable: code ? Number(code) : null,
        };
      }),
    };
    await handleUpdateVente(id, submitData);
    toast.success("Mise à jour réussie. Tous les calculs comptables ont été mis à jour.");
    navigate(-1);
  };

  const nom = (
    <div style={{ display: "flex" }}>
      <h4 className="titre-dossier">
        Modification d&apos;entrée: {venteDetail?.data?.[0]?.id || "Chargement..."}
      </h4>
    </div>
  );

  if (isLoadingVente) return <div>Chargement des détails de vente...</div>;
  if (venteError) return <div>Une erreur est survenue pour la vente.</div>;

  return (
    <div className="vente-detail-container">
      <div className="d-flex align-items-center">
        <HeaderFirst
          nom={nom}
          description={
            <div>Modifiez les informations de cette entrée. Tous les calculs comptables seront mis à jour automatiquement.</div>
          }
        />
      </div>
      <form
        className="form-layout"
        onSubmit={onSubmit}
        style={{ padding: "15px" }}
      >
        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Client"
              name="client"
              disabled
              value={venteDetail.data[0].client_details.nom}
              className={"custom-input"}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Date"
              name="date"
              type="text"
              disabled
              value={updateData.date}
              className={"custom-input"}
            />
          </div>
        </div>

        <div className="input-row row">
          <div className="col-md-6">
            <InputDetail
              label="Intitulé"
              name="intitule"
              value={updateData.intitule}
              onChange={(e) =>
                setUpdateData({ ...updateData, intitule: e.target.value })
              }
              className={"custom-input"}
            />
          </div>
          <div className="col-md-6">
            <InputDetail
              label="Description"
              name="description"
              value={updateData.description}
              onChange={(e) =>
                setUpdateData({ ...updateData, description: e.target.value })
              }
              className={"custom-input"}
            />
          </div>
        </div>

        {updateData.produits_associes &&
        updateData.produits_associes.length > 0 ? (
          updateData.produits_associes.map((prod, index) => (
            <div key={`${prod.id}-${index}`} className="row flex-row justify-content-between" style={{ marginBottom: "15px" }}>
              <div className="col-4">
                <AutocompleteSelect
                  label="Produit"
                  options={produitsData?.data || []}
                  value={prod.id}
                  onChange={(value) => handleProduitChange(index, value)}
                  placeholder="Sélectionnez un produit"
                />
              </div>
              <div className="col-2">
                <InputDetail
                  label="Quantité"
                  name={`quantite-${prod.id}`}
                  type="number"
                  value={prod.quantite}
                  onChange={(e) =>
                    handleQuantiteChange(index, parseFloat(e.target.value) || 0)
                  }
                  className={"custom-input"}
                />
              </div>
              <div className="col-3">
                <InputDetail
                  label="Prix"
                  name={`prix-${prod.id}`}
                  type="number"
                  value={prod.prix}
                  onChange={(e) => handlePrixChange(index, parseFloat(e.target.value) || 0)}
                  className={"custom-input"}
                />
              </div>
              <div className="col-3">
                <AutocompleteSelect
                  label="Compte comptable"
                  options={accountOptions}
                  value={prod.code_comptable ?? null}
                  onChange={(value) => handleCompteChange(index, value)}
                  createText="Utiliser"
                  onCreateNew={async (name) => {
                    const digits = String(name).replace(/\D/g, "");
                    if (!digits) return null;
                    return { id: Number(digits), nom: String(name).trim() };
                  }}
                  placeholder="Ex: 706000 (vide = compte du produit)"
                />
              </div>
            </div>
          ))
        ) : (
          <p>Aucun produit associé.</p>
        )}

        <div className="input-row d-flex">
          <div className="pieceContainer col-md-6">
            <p
              style={{ color: "#068F7A", fontWeight: "bold" }}
              className="form-label"
            >
              Pièce jointe
            </p>
            <Link to={venteDetail?.data[0]?.piece_joint}>
              <div className="card py-3">
                <div className="card-body d-flex align-items-center justify-content-center text-center">
                  <FilePreview filePath={venteDetail.data[0]?.piece_joint} />
                </div>
              </div>
            </Link>
          </div>
          {venteDetail?.data[0]?.justificatif_file && (
            <div className="pieceContainer col-md-6 ms-3">
              <p
                style={{ color: "#068F7A", fontWeight: "bold" }}
                className="form-label"
              >
                Justificatif de règlement
              </p>
              <Link to={venteDetail?.data[0]?.justificatif_file}>
                <div className="card py-3">
                  <div className="card-body d-flex align-items-center justify-content-center text-center">
                    <FilePreview filePath={venteDetail.data[0]?.justificatif_file} />
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>

        <div className="form-navigation-buttons">
          <button type="submit" className="next-button">
            Modifier
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailModifPage;
